import { IconButton, InputAdornment, Typography } from '@material-ui/core';
import { useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import QrCodeIcon from '../../../../../core/src/media/icons/QrCodeIcon';
import { getDistanceUnitLabel } from '../../../../../core/src/util';
import { Checkbox } from '../../util/form2/Checkbox';
import { SelectHubType } from '../../util/form2/SelectHubType';
import { TextField } from '../../util/form2/TextField';
import {
  HubFormAMarker,
  HubFormBMarker,
  HubFormBuildingImage,
  HubFormCMarker,
} from './HubFormSVGImages';

/** Helper text explaining where to measure the hub offset distance */
export const HUB_OFFSET_HELPER_TEXT = (
  <>
    Input distance between the <u> Bottom of the Headspace Sensor </u>and the
    <u> Top of the Top Ring </u>of the Bin :
  </>
);

/**
 * A form component for assigning and configuring hub settings in a grain container system.
 * Handles input fields for hub identification, type selection, and sensor configurations.
 * Provides additional fields for dual-point sensor measurements when enabled.
 * Uses Formik context for form state management.
 */
export const AssignHubFormFields: React.FunctionComponent<{
  /** Flag indicating if this is an update operation rather than a new assignment */
  update?: boolean;
  /** Callback function to open QR code scanner dialog for hub ID input */
  openQRScannerDialog?: (targetTextField?: string) => void;
  /** Name prefix for Formik fields, useful when used in FieldArray */
  namePrefix?: string;
}> = ({ update, openQRScannerDialog, namePrefix = '' }) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const distanceUnitLabel = getDistanceUnitLabel();

  const getFieldName = (name: string) => (namePrefix ? `${namePrefix}.${name}` : name);
  const getFieldValue = (name: string) => {
    if (namePrefix) {
      const index = namePrefix.split('.').pop();
      if (values && values.hubs && index !== undefined && values.hubs[index]) {
        return values.hubs[index][name];
      }
      return undefined;
    }
    return values ? values[name] : undefined;
  };

  useEffect(() => {
    // reset hub_offset_ft when has_level_sensor is unchecked
    if (!getFieldValue('has_level_sensor')) {
      setFieldValue(getFieldName('hub_offset_ft'), 0);
    }
  }, [getFieldValue('has_level_sensor')]);
  return (
    <>
      <div>
        {!update && (
          <TextField
            name={getFieldName('core_id')}
            id="assign-hub"
            label="Hub ID"
            placeholder="AmAg_00-0-000"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="open QR code scanner"
                    onClick={() =>
                      openQRScannerDialog && openQRScannerDialog(getFieldName('core_id'))
                    }
                  >
                    <QrCodeIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            fullWidth
            required
          />
        )}
        <TextField
          style={{ marginTop: 10 }}
          name={getFieldName('nickname')}
          label="Hub Nickname"
          placeholder="Headspace Hub"
          fullWidth
          required
        />
        <SelectHubType
          style={{ marginTop: 10 }}
          name={getFieldName('hub_type')}
          label="Hub Type"
          placeholder="None Selected"
          fullWidth
          required
        />
      </div>
      <Checkbox
        style={{ marginTop: 10, width: '100%' }}
        name={getFieldName('has_level_sensor')}
        label="Headspace Level Sensor Add-on Equipped"
      />
      {getFieldValue('has_level_sensor') && (
        <div style={{ paddingLeft: 32 }}>
          <TextField
            required
            fullWidth
            style={{ marginTop: 10 }}
            name={getFieldName('sidewall_headspace_distance_ft')}
            label="Sidewall to Headspace Sensor Distance (in)"
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  style={{ position: 'absolute', left: -32, marginRight: 8 }}
                >
                  <HubFormAMarker />
                </InputAdornment>
              ),
            }}
            inputProps={{
              inputMode: 'decimal',
            }}
            type="number"
          />
          <TextField
            required
            fullWidth
            style={{ marginTop: 10 }}
            name={getFieldName('headspace_cablesensor_length_ft')}
            label="Headspace Sensor Cable Length (in)"
            inputProps={{
              inputMode: 'decimal',
            }}
            type="number"
          />

          {/*<Typography variant="subtitle2" style={{ marginBottom: 5, marginTop: 5 }}>*/}
          {/*  {HUB_OFFSET_HELPER_TEXT}*/}
          {/*</Typography>*/}
          {/*<TextField*/}
          {/*  required*/}
          {/*  fullWidth*/}
          {/*  name="hub_offset_ft"*/}
          {/*  label={`Hub Offset (${distanceUnitLabel})`}*/}
          {/*  inputProps={{*/}
          {/*    inputMode: 'decimal',*/}
          {/*  }}*/}
          {/*  type="number"*/}
          {/*/>*/}
        </div>
      )}
      <Checkbox
        style={{ marginTop: 10, width: '100%' }}
        name={getFieldName('has_aux_level_sensor')}
        label="Auxiliary Level Sensor Add-on Equipped"
      />

      {/* Conditional rendering block that shows additional configuration fields when dual-point sensor is enabled.
          These fields include various distance measurements and offsets required for proper sensor positioning and calibration,
          such as hub offset, sidewall distances, cable lengths, and axis offsets. All measurements use the same distance unit. */}
      {getFieldValue('has_aux_level_sensor') && (
        <div style={{ paddingLeft: 32 }}>
          <TextField
            required
            fullWidth
            style={{ marginTop: 10 }}
            name={getFieldName('sidewall_auxilary_distance_ft')}
            label="Sidewall to Auxiliary Level Sensor Distance (in)"
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  style={{ position: 'absolute', left: -32, marginRight: 8 }}
                >
                  <HubFormBMarker />
                </InputAdornment>
              ),
            }}
            inputProps={{
              inputMode: 'decimal',
            }}
            type="number"
          />
          <TextField
            required
            fullWidth
            style={{ marginTop: 10 }}
            name={getFieldName('aux_cablesensor_length_ft')}
            label="Auxiliary Level Sensor Cable Length (in)"
            inputProps={{
              inputMode: 'decimal',
            }}
            type="number"
          />
        </div>
      )}
      {(getFieldValue('has_aux_level_sensor') || getFieldValue('has_level_sensor')) && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 20,
              marginBottom: 20,
            }}
          >
            <HubFormBuildingImage />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <TextField
              required
              style={{ marginTop: 10, width: 250 }}
              name={getFieldName('roof_angle_degrees')}
              label="Roof Pitch Angle (°)"
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    style={{ position: 'absolute', left: -32, marginRight: 8 }}
                  >
                    <HubFormCMarker />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                inputMode: 'decimal',
              }}
              type="number"
            />
          </div>
        </>
      )}
    </>
  );
};
